import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function PublicBooking() {
  const { email } = useParams();
  const [owner, setOwner] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", date: "", time: "09:00", duration: 30, notes: "" });
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(`/public/book/${encodeURIComponent(email)}`).then(({ data }) => setOwner(data)).catch(() => setOwner(false));
  }, [email]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const start = new Date(`${form.date}T${form.time}:00`);
      const end = new Date(start.getTime() + Number(form.duration) * 60000);
      await api.post(`/public/book/${encodeURIComponent(email)}`, {
        name: form.name, email: form.email,
        start: start.toISOString(), end: end.toISOString(),
        notes: form.notes,
      });
      setDone(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  if (owner === false) return <div className="min-h-screen flex items-center justify-center bg-[var(--peak-bg)]"><div className="section-label">User not found</div></div>;
  if (!owner) return <div className="min-h-screen flex items-center justify-center bg-[var(--peak-bg)]"><div className="section-label">Loading…</div></div>;

  return (
    <div className="min-h-screen bg-[var(--peak-bg)] flex items-center justify-center p-6" data-testid="public-booking-page">
      <div className="max-w-md w-full bg-white border-2 border-[var(--peak-ink)] p-8">
        <div className="section-label">Book a slot with</div>
        <h1 className="font-heading font-black text-3xl tracking-tighter uppercase">{owner.name || owner.email}</h1>

        {done ? (
          <div className="mt-6" data-testid="booking-success">
            <div className="section-label text-[#008A27]">Booking confirmed</div>
            <div className="text-lg font-semibold mt-1">You'll receive a follow-up soon.</div>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <label className="section-label">Your name</label>
              <input required data-testid="pb-name" className="brutalist-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <label className="section-label">Email</label>
              <input required type="email" data-testid="pb-email" className="brutalist-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="section-label">Date</label>
                <input required type="date" data-testid="pb-date" className="brutalist-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
              </div>
              <div>
                <label className="section-label">Time</label>
                <input required type="time" data-testid="pb-time" className="brutalist-input" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} />
              </div>
              <div>
                <label className="section-label">Minutes</label>
                <input required type="number" data-testid="pb-duration" className="brutalist-input" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="section-label">Notes</label>
              <textarea rows={2} data-testid="pb-notes" className="w-full border border-[var(--peak-border)] p-2" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            <button type="submit" disabled={loading} data-testid="pb-submit" className="w-full bg-[var(--peak-accent)] hover:bg-black text-white font-bold uppercase tracking-widest px-6 py-3 disabled:opacity-60">
              {loading ? "Booking…" : "Confirm booking"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
